/**
 * Chat Store
 *
 * Manages chat messages, streaming state, and model configuration.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { ChatMessage, ModelConfig, ChatContext } from '@/types';

interface ChatState {
  // Messages
  messages: ChatMessage[];

  // Streaming state
  isStreaming: boolean;
  streamingMessageId: string | null;

  // Model configuration
  modelConfig: ModelConfig;

  // Context
  context: ChatContext;

  // Actions - Messages
  addMessage: (message: ChatMessage) => void;
  appendToMessage: (messageId: string, content: string) => void;
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
  clearMessages: () => void;
  removeMessage: (messageId: string) => void;

  // Actions - Streaming
  startStreaming: (messageId: string) => void;
  stopStreaming: () => void;

  // Actions - Configuration
  setModelConfig: (config: Partial<ModelConfig>) => void;
  setDefaultModel: (model: string) => void;

  // Actions - Context
  setContext: (context: Partial<ChatContext>) => void;
  clearContext: () => void;

  // Getters
  getLastMessage: () => ChatMessage | undefined;
  getMessageById: (id: string) => ChatMessage | undefined;
}

const DEFAULT_MODEL_CONFIG: ModelConfig = {
  defaultModel: 'node1-vllm',
  temperature: 0.7,
  maxTokens: 4096,
  streamingEnabled: true,
};

const DEFAULT_CONTEXT: ChatContext = {
  projectId: null,
  activeFilePath: null,
  selectedCode: null,
};

export const useChatStore = create<ChatState>()(
  immer((set, get) => ({
    // Initial state
    messages: [],
    isStreaming: false,
    streamingMessageId: null,
    modelConfig: DEFAULT_MODEL_CONFIG,
    context: DEFAULT_CONTEXT,

    // Message actions
    addMessage: (message) =>
      set((state) => {
        state.messages.push(message);
      }),

    appendToMessage: (messageId, content) =>
      set((state) => {
        const message = state.messages.find((m) => m.id === messageId);
        if (message) {
          message.content += content;
        }
      }),

    updateMessage: (messageId, updates) =>
      set((state) => {
        const index = state.messages.findIndex((m) => m.id === messageId);
        if (index !== -1) {
          state.messages[index] = { ...state.messages[index], ...updates };
        }
      }),

    clearMessages: () =>
      set((state) => {
        state.messages = [];
      }),

    removeMessage: (messageId) =>
      set((state) => {
        state.messages = state.messages.filter((m) => m.id !== messageId);
      }),

    // Streaming actions
    startStreaming: (messageId) =>
      set((state) => {
        state.isStreaming = true;
        state.streamingMessageId = messageId;
        const message = state.messages.find((m) => m.id === messageId);
        if (message) {
          message.isStreaming = true;
        }
      }),

    stopStreaming: () =>
      set((state) => {
        if (state.streamingMessageId) {
          const message = state.messages.find(
            (m) => m.id === state.streamingMessageId
          );
          if (message) {
            message.isStreaming = false;
          }
        }
        state.isStreaming = false;
        state.streamingMessageId = null;
      }),

    // Configuration actions
    setModelConfig: (config) =>
      set((state) => {
        state.modelConfig = { ...state.modelConfig, ...config };
      }),

    setDefaultModel: (model) =>
      set((state) => {
        state.modelConfig.defaultModel = model;
      }),

    // Context actions
    setContext: (context) =>
      set((state) => {
        state.context = { ...state.context, ...context };
      }),

    clearContext: () =>
      set((state) => {
        state.context = DEFAULT_CONTEXT;
      }),

    // Getters
    getLastMessage: () => {
      const messages = get().messages;
      return messages.length > 0 ? messages[messages.length - 1] : undefined;
    },

    getMessageById: (id) => {
      return get().messages.find((m) => m.id === id);
    },
  }))
);
